import { createSelector } from '@lumapps/redux/reselect';

import { isFeatureEnabled } from '../ducks/selectors';

interface RolloutTokens {
    /** Feature flag token for general availability (GA). Acts as the master safety net. */
    ga: string;
    /** Feature flag token for the early-access stage (stable demos, pre-release). */
    earlyAccess: string;
    /** Feature flag token for the development stage (unstable, dev-only). */
    development: string;
}

/**
 * Creates a set of rollout-stage selectors following the ADR inclusive hierarchy model.
 * https://docs.google.com/document/d/1BqZnLFoPS968P-ubFNmOXIXAk1n6ghaKLiFyTpbfmHQ/edit?usp=sharing
 *
 * - `isGA`:          GA flag is enabled. Use for production-ready features.
 * - `isEarlyAccess`: GA AND (earlyAccess OR development). Use for stable pre-release features.
 *                    Development environments automatically see early-access features.
 * - `isDevelopment`: GA AND development. Use for unstable, in-progress features.
 *
 * The GA flag acts as a master kill switch: `isEarlyAccess` and `isDevelopment`
 * always return false when GA is disabled, even if stage flags are enabled.
 *
 * @example
 * const { isGA, isEarlyAccess, isDevelopment } = createRolloutSelectors({
 *     ga: 'my-feature',
 *     earlyAccess: 'my-feature-early-access',
 *     development: 'my-feature-development',
 * });
 *
 * // Use directly as a selector
 * export const isMyFeatureEnabled = isGA;
 *
 * // Compose with other selectors
 * export const canAccessMyFeatureAdmin = createSelector(
 *     isGA,
 *     isSuperAdmin,
 *     (enabled, superAdmin) => superAdmin && enabled,
 * );
 */
export const createRolloutSelectors = (tokens: RolloutTokens) => {
    const isGAFlag = isFeatureEnabled(tokens.ga);
    const isEAFlag = isFeatureEnabled(tokens.earlyAccess);
    const isDevFlag = isFeatureEnabled(tokens.development);

    const isGA = isGAFlag;

    const isEarlyAccess = createSelector(isGAFlag, isEAFlag, isDevFlag, (ga, ea, dev) => ga && (ea || dev));

    const isDevelopment = createSelector(isGAFlag, isDevFlag, (ga, dev) => ga && dev);

    return { isGA, isEarlyAccess, isDevelopment } as const;
};
